import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { List, ListItem, Searchbar } from 'framework7-react';

import { I18n } from 'Locales';
import { User } from 'Repositories';
import { ColumnView, RowView } from 'Containers';
import { debounce, FormattedLabel } from 'Helpers';
import { COUNTRIES_ADDRESS, REGIONS } from 'Constants';

import { getFieldProp } from '../../shared';
import { AddressFields } from './components';
import UnavailableComponent from '../UnavailableComponent';

import './style.scss';

const Address = props => {
  const [searchAddress, setSearchAddress] = useState('');
  const [enableAddressFields, setEnableAddressFields] = useState(false);
  const setValues = (oldProps, individualKeyValue, newProps, searchProps = false) => {
    const initialState = {
      search_address: '',
      City: '',
      AddressLine1: '',
      AddressLine2: '',
      AddressLine3: '',
      PostCode: ''
    };

    let newState;

    if (searchProps) {
      const newPropsMapped = {
        AddressLine1: newProps?.address_line1 || '',
        AddressLine2: newProps?.address_line2 || '',
        AddressLine3: newProps?.address_line3 || '',
        PostCode: newProps?.postcode || '',
        search_address: newProps?.full_address,
        City: newProps?.town || ''
      };

      newState = { ...initialState, ...newPropsMapped };
    } else {
      newState = { ...initialState, ...oldProps, ...individualKeyValue, search_address: oldProps.search_address };
    }

    props.updateValue({ value: newState });
  };

  const handleLoqateSearch = debounce((getLoqateAddress, country, e) => {
    const text = e.target.value.trim();
    if (text.length >= 1) {
      getLoqateAddress({ text, country });
    }
  }, 500);

  const onClickLoqateHandler = ({ country, item, updateTransientProps }) => {
    const data = { ...item, country };

    User.getLoqateAddress(data).then(cb => {
      if (cb.status === 200) {
        const address_uk_find = cb.data[0];

        if (address_uk_find) {
          setValues(null, { full_address: address_uk_find?.full_address }, address_uk_find, true);
          updateTransientProps({
            loqate_address: [],
            addressFoundWithLoqate: true,
            address_field_disabled: true
          });
        } else {
          setValues(null, { full_address: '' }, item || {}, true);
          updateTransientProps({
            address_field_disabled: false,
            addressFoundWithLoqate: false,
            address_not_found: true,
            manual_address: true,
            loqate_address: []
          });
        }
      }
    });
  };

  const handleSearch = (updateTransientProps, e) => {
    const value = e.target.value.toLowerCase().trim();

    if (value.length === 0) {
      updateTransientProps({ filtered_addresses: [] });
      return;
    }

    const searchParts = value.split(/\s+/);
    /* Split search input by whitespace (by adding this
     we'll be able to search for an address by using comma or not) */

    const filteredAddresses = props.currentCheckInAdress
      ?.filter(({ full_address }) => {
        const fullAddress = full_address.toLowerCase();
        return searchParts.every(part => fullAddress.includes(part));
      })
      .slice(0, 10);

    updateTransientProps({ filtered_addresses: filteredAddresses });
    setSearchAddress(value);
  };

  const onClickHandler = ({ updateTransientProps, setSearchAddress, item }) => {
    setValues(null, null, item, true);
    setSearchAddress('');
    updateTransientProps({
      filtered_addresses: []
    });
  };

  const setManualAddress = ({ enableAddressFields, setEnableAddressFields, updateTransientProps }) => {
    setEnableAddressFields(!enableAddressFields);
    updateTransientProps({ manual_address: !enableAddressFields });
  };

  const { country, fields, getLoqateAddress, isOffline, name, required, transient, updateTransientProps } = props;
  const { address_field_disabled = true, address_not_found = false } = transient;

  useEffect(() => {
    setEnableAddressFields(address_not_found);
  }, [address_not_found]);

  useEffect(() => {
    if (isOffline && COUNTRIES_ADDRESS.FINDER.includes(country)) {
      updateTransientProps({ manual_address: true, addressFoundWithLoqate: false });
    }
  }, [isOffline]);

  return COUNTRIES_ADDRESS.ALL.includes(country) ? (
    <div>
      <RowView justifyContent={'flex-start'} paddingLeft={16}>
        <div className="item-title item-label">{FormattedLabel({ required, name })}</div>
      </RowView>

      {(REGIONS.UK.includes(country) || REGIONS.US.includes(country)) && (
        <>
          {!isOffline && (
            <div id={'address-comp'}>
              <RowView paddingHorizontal={8}>
                <Searchbar
                  init={false}
                  placeholder={getFieldProp(fields, 'search_address').placeholder}
                  onChange={handleLoqateSearch.bind(null, getLoqateAddress, country)}
                  onClickClear={updateTransientProps.bind(null, { loqate_address: [] })}
                />
              </RowView>

              <div className="px-16">
                {!address_field_disabled &&
                  !transient?.loqate_address?.length > 0 &&
                  I18n.t('flow:components.address.loqateDown')}
              </div>
            </div>
          )}

          {transient.loqate_address && !!transient.loqate_address.length && !isOffline && (
            <List className="mt-0">
              {transient.loqate_address.map((item, index) => {
                const { id, type, full_address } = item;
                const address_params = {
                  country,
                  id,
                  type,
                  text: full_address.split('(More Addresses)')[0].trim()
                };

                return (
                  <ListItem
                    key={index}
                    onClick={
                      item.type === 'Address'
                        ? onClickLoqateHandler.bind(null, { country, item, updateTransientProps })
                        : getLoqateAddress.bind(null, address_params)
                    }>
                    {item.full_address}
                  </ListItem>
                );
              })}
            </List>
          )}
        </>
      )}

      {COUNTRIES_ADDRESS.AUTOCOMPLETE.includes(country) && (
        <>
          <div id={'address-comp'}>
            <RowView paddingHorizontal={8}>
              <ColumnView justifyContent={'flex-start'} alignItems={'stretch'} gap={8} marginTop={8}>
                {transient?.filtered_addresses && !isOffline && (
                  <List className="mt-0 ">
                    <ListItem
                      className={`pl-8 ${enableAddressFields ? 'disabled' : ''}`}
                      checkbox
                      disabled={enableAddressFields}
                      value={enableAddressFields}
                      onChange={setManualAddress.bind(null, {
                        enableAddressFields,
                        setEnableAddressFields,
                        updateTransientProps
                      })}
                      title={I18n.t('flow:components.address.couldNotFindAddress')}
                    />
                  </List>
                )}

                <Searchbar
                  init={false}
                  placeholder={getFieldProp(fields, 'search_address').placeholder}
                  onChange={handleSearch.bind(null, updateTransientProps)}
                  onClickClear={updateTransientProps.bind(null, { filtered_addresses: [] })}
                />
              </ColumnView>
            </RowView>
          </div>

          {transient?.filtered_addresses?.length ? (
            <List className="mt-0 mx-16 dropdown">
              {transient.filtered_addresses.map((item, index) => (
                <ListItem
                  key={index}
                  onClick={onClickHandler.bind(null, { updateTransientProps, setSearchAddress, item })}>
                  {item.full_address}
                </ListItem>
              ))}
            </List>
          ) : (
            searchAddress && <div className="empty-list">{I18n.t('general:noResultsFound')}</div>
          )}
        </>
      )}

      <AddressFields
        {...props}
        setValues={setValues}
        enableAddressFields={enableAddressFields}
        addressFoundWithLoqate={transient.addressFoundWithLoqate}
      />
    </div>
  ) : (
    <UnavailableComponent {...props} />
  );
};

Address.propTypes = {
  component_key: PropTypes.any,
  country: PropTypes.string,
  currentCheckInAdress: PropTypes.array,
  fields: PropTypes.array,
  getLoqateAddress: PropTypes.func,
  isOffline: PropTypes.bool,
  name: PropTypes.string,
  required: PropTypes.bool,
  shouldShowError: PropTypes.bool,
  transient: PropTypes.object,
  updateTransientProps: PropTypes.func,
  updateValue: PropTypes.func,
  value: PropTypes.oneOfType(PropTypes.string, PropTypes.object)
};

export default Address;
